import { useEffect } from 'react'
import { useDispatch } from 'react-redux'
import { useAppWeb3 } from '../../hooks'
import { useAddPopup } from '../application/hooks'
import { isTxReceiptDebugEnabled, txReceiptDebug, txReceiptWarn } from '../../utils/debugTxReceipt'
import { invalidateMulticallEntries } from '../multicall/actions'
import store, { AppDispatch } from '../index'
import { checkedTransaction, finalizeTransaction, SerializableTransactionReceipt } from './actions'
import { fetchSerializableReceipt } from './receiptRpc'

/** Usado en tests. */
export function shouldCheck(
  lastBlockNumber: number,
  tx: { addedTime: number; receipt?: SerializableTransactionReceipt; lastCheckedBlockNumber?: number },
): boolean {
  if (tx.receipt) return false
  if (!tx.lastCheckedBlockNumber) return true
  const blocksSinceCheck = lastBlockNumber - tx.lastCheckedBlockNumber
  if (blocksSinceCheck < 1) return false
  const minutesPending = (new Date().getTime() - tx.addedTime) / 1000 / 60
  if (minutesPending > 60) {
    return blocksSinceCheck > 9
  } else if (minutesPending > 5) {
    return blocksSinceCheck > 2
  } else {
    return true
  }
}

/** Éxito en popup: EIP-658 status 1; si falta campo (pre-Byzantium), asumimos éxito si no es fallo explícito. */
function receiptSuccessForPopup(status: number | undefined): boolean {
  if (status === undefined) return true
  return status === 1
}

export default function Updater(): null {
  const { chainId, library } = useAppWeb3()

  const dispatch = useDispatch<AppDispatch>()
  const addPopup = useAddPopup()

  useEffect(() => {
    if (!chainId || !library) return undefined

    const POLL_MS = 2800

    txReceiptDebug('effect mount', {
      chainId,
      hasLibrary: !!library,
      notes:
        'Un eth_getTransactionReceipt por tx pendiente por ciclo vía proveedor de la wallet (parseo JSON seguro); sin segundo nodo RPC ni intento ethers.',
      debugFlag: isTxReceiptDebugEnabled()
        ? 'on (localStorage DEBUG_TX_RECEIPT or VITE_DEBUG_TX_RECEIPT)'
        : 'off — see docs/500-guias/logs-debug-y-flags.md',
    })

    /** En navegador `setInterval` devuelve `number`; evitar conflicto con `NodeJS.Timeout` del typings de Node. */
    let intervalId: number | undefined

    const pollReceipts = () => {
      const txs = store.getState().transactions[chainId] ?? {}
      const blockHint = store.getState().application.blockNumber[chainId] ?? 0
      const pendingKeys = Object.keys(txs).filter((h) => !txs[h]?.receipt)

      if (pendingKeys.length > 0) {
        txReceiptDebug('poll tick', {
          chainId,
          pendingCount: pendingKeys.length,
          hashes: pendingKeys,
          blockHintRedux: blockHint,
        })
      }

      pendingKeys.forEach((hash) => {
        txReceiptDebug('eth_getTransactionReceipt:start', { hash })

        fetchSerializableReceipt(library, hash)
          .then((receipt) => {
            if (!receipt) {
              txReceiptDebug('no receipt yet → checkedTransaction', { hash, blockHint })
              dispatch(checkedTransaction({ chainId, hash, blockNumber: blockHint }))
              return
            }

            const latest = store.getState().transactions[chainId]?.[hash]
            if (!latest) {
              txReceiptWarn('finalize skipped: tx missing from store', { hash, chainId })
              return
            }
            if (latest.receipt) {
              txReceiptDebug('finalize skipped: already has receipt', { hash })
              return
            }

            txReceiptDebug('finalizeTransaction + popup', {
              hash,
              summary: latest.summary,
              receiptStatus: receipt.status,
              chainId,
            })

            dispatch(
              finalizeTransaction({
                chainId,
                hash,
                receipt,
              }),
            )

            // Si la tx revertió en cadena, los reads cacheados siguen siendo coherentes; solo purgamos tras éxito real.
            const invalidateCalls = latest.multicallInvalidateCalls
            if (invalidateCalls && invalidateCalls.length > 0 && receiptSuccessForPopup(receipt.status)) {
              dispatch(invalidateMulticallEntries({ chainId, calls: invalidateCalls }))
            }

            addPopup(
              {
                txn: {
                  hash,
                  success: receiptSuccessForPopup(receipt.status),
                  summary: latest.summary,
                },
              },
              hash,
            )
          })
          .catch((error) => {
            txReceiptWarn('poll chain error', { hash, error })
          })
      })
    }

    /** Solo hay intervalo de polling cuando existe al menos una tx pendiente (evita llamadas RPC en bucle vacío). */
    const syncPollingFromStore = () => {
      const txs = store.getState().transactions[chainId] ?? {}
      const hasPending = Object.keys(txs).some((h) => !txs[h]?.receipt)

      if (!hasPending) {
        if (intervalId !== undefined) {
          window.clearInterval(intervalId)
          intervalId = undefined
          txReceiptDebug('polling stopped (no pending txs)')
        }
        return
      }

      if (intervalId === undefined) {
        pollReceipts()
        intervalId = window.setInterval(pollReceipts, POLL_MS)
        txReceiptDebug('polling started (pending txs)')
      }
    }

    const unsubscribe = store.subscribe(syncPollingFromStore)
    syncPollingFromStore()

    const onVisible = () => {
      if (document.visibilityState !== 'visible') return
      const txs = store.getState().transactions[chainId] ?? {}
      if (Object.keys(txs).some((h) => !txs[h]?.receipt)) {
        pollReceipts()
      }
    }
    document.addEventListener('visibilitychange', onVisible)

    return () => {
      unsubscribe()
      if (intervalId !== undefined) window.clearInterval(intervalId)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [chainId, library, dispatch, addPopup])

  return null
}
